import { useEffect, useState } from 'react'
import { api, type AccessRole } from '../lib/api.ts'
import { label, PERMISSION_LABEL } from '../lib/vocabulary.ts'
import { Button } from '../components/Button.tsx'
import { DataTable, type Column } from '../components/DataTable.tsx'
import { icons } from '../components/icons.ts'
import { Tag } from '../components/Tag.tsx'

/**
 * Funções: which conferências each person here sees (#280, ADR 0024).
 *
 * A list like any other; opening a row is where it is renamed and its
 * permissions chosen. "Função" rather than "papel" because the papel is
 * already the other thing: which screens somebody reaches, not which work
 * they see. Who holds which role is chosen on the users screen.
 *
 * New roles are ours to create for now, so the button is offered only to a
 * super admin -- the server refuses anybody else regardless.
 */
export function Roles({
  canCreate,
  onBack,
  onOpen,
  onCreate,
}: {
  canCreate: boolean
  onBack: () => void
  onOpen: (id: string) => void
  onCreate: () => void
}) {
  const [roles, setRoles] = useState<AccessRole[]>([])
  const [error, setError] = useState<string | undefined>()
  const [loading, setLoading] = useState(true)

  function load() {
    setLoading(true)
    setError(undefined)
    void api
      .roles()
      .then((r) => setRoles(r.roles))
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Fila
        </Button>
        <h1 className="text-tH4 text-text-strong">Funções</h1>
        {canCreate && (
          <Button
            className="ml-auto"
            variant="primary"
            tone="brand"
            size="small"
            leadingIcon={icons.add}
            onClick={onCreate}
          >
            Nova função
          </Button>
        )}
      </div>

      <p className="text-tSmall text-text-weak max-w-[70ch]">
        Cada função diz quais conferências quem a tem enxerga: em que etapa, e se de todos ou só as
        próprias. Quem tem cada função se escolhe em Usuários.
      </p>

      <DataTable
        columns={COLUMNS}
        rows={roles}
        rowKey={(r) => r.id}
        loading={loading}
        error={error}
        onRetry={load}
        onRowClick={(r) => onOpen(r.id)}
        pageSize={4}
        minWidth="40rem"
        empty="Nenhuma função."
      />
    </div>
  )
}

const COLUMNS: ReadonlyArray<Column<AccessRole>> = [
  {
    key: 'name',
    header: 'Nome',
    render: (r) => (
      <span className="flex items-center gap-2">
        {r.name}
        {r.system && <Tag tone="neutral">do sistema</Tag>}
      </span>
    ),
  },
  {
    key: 'permissions',
    header: 'Permissões',
    className: 'text-tSmall text-text-weak',
    render: (r) =>
      r.permissions.length === 0
        ? 'nenhuma — não vê nada'
        : r.permissions.map((one) => label(PERMISSION_LABEL, one)).join(' · '),
  },
  {
    key: 'members',
    header: 'Usuários',
    className: 'tabular-nums text-text-weak',
    render: (r) => r.members,
  },
]
